import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { RootTest } from 'dialer';
import { firstValueFrom, Subscription } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';
import { AppState } from '../../core/app-state';
import { DriveTestPointSeq, PathTestPointSeq, TaskTypeName } from '../../core/models';
import {  DriveTestSeqService } from '../../core/services';
import { DialerClientService } from '../../core/services/dialer-client-service';
import { TestSeqService } from '../../core/services/test-seq.service';


@Component({
  selector: 'app-dialer-client-testseq',
  templateUrl: './dialer-client-testseq.component.html',
  styleUrls: ['./dialer-client-testseq.component.scss']
})
export class DialerClientTestSeqComponent implements OnInit, OnDestroy {

  @Input('seqContext') set _seqContextSetter(context: {type:TaskTypeName,seq: PathTestPointSeq | DriveTestPointSeq}) {
    if (this.seq?.id !== context.seq?.id || this.seq?.dialerSeqId !== context.seq?.dialerSeqId) {
      this.watchForResults(context.seq,context.type)
    }
  }

  @Output('rootTest') rootTest$ = new EventEmitter<RootTest>()

  loading = false
  warningMsg: string
  seq: PathTestPointSeq | DriveTestPointSeq
  rootTest: RootTest

  testSeqSubscription: Subscription
  loadFromDialerSub: Subscription

  constructor(
    public dialerClientService: DialerClientService,
    public _testSeqService: TestSeqService,
    public _driveTestSeqService: DriveTestSeqService,
    public appState: AppState) {
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.testSeqSubscription?.unsubscribe()
    this.loadFromDialerSub?.unsubscribe()
  }
  async watchForResults(seq: PathTestPointSeq | DriveTestPointSeq, taskType: TaskTypeName) {
    this.loading = false
    this.seq = seq
    this.rootTest = null
    this.rootTest$.emit(this.rootTest)
    this.loadFromDialerSub?.unsubscribe()
    this.testSeqSubscription?.unsubscribe()
    if (!seq?.dialerSeqId) {
      return
    }
    this.loading = true

    // TODO - remove this workaround...
    // "dialer call log" post processing view opens this component (click on sequence id) but it doesn't know task type
    if ( !taskType ) {
      const pathTestSeqDoc = await this._testSeqService.rxCollection.findOne(seq.dialerSeqId).exec();
      if ( pathTestSeqDoc ) {
        taskType = 'path test'
      } else if ( await this._driveTestSeqService.rxCollection.findOne(seq.dialerSeqId).exec() ) {
        taskType = 'drive test'
      } else {
        const msg = `Sequence type unknown and could find the sequence in database (dialerSeqId=${seq.dialerSeqId}).`;
        console.warn(msg)
        this.warningMsg = msg
        this.loading = false
        return
      }
    }

    if (taskType == "drive test") {
      this.testSeqSubscription = this._driveTestSeqService.rxCollection.findOne(seq.dialerSeqId).$.subscribe(async testSeqDoc => {
        this.loading = false
        this.warningMsg = null
        this.rootTest = null
        this.rootTest$.emit(this.rootTest)
        if (testSeqDoc) {
          this.rootTest = testSeqDoc.dialer
          // dialer data may be null to save space on clients...
          if (!testSeqDoc.dialer && testSeqDoc.endTime) {
            if (this.appState.isOffline()) {
              this.warningMsg = "Dialer data not available while offline"
            } else {
              try {
                //if type ==drive test then use drive test seq service else use test seq service
                const testSeq = await this._driveTestSeqService.getByIdViaRest(testSeqDoc.seqid)
                console.log('getByIdViaRest()')
                console.dir(testSeq)
                if (testSeq.dialer) {
                  this.rootTest = testSeq.dialer
                }
              } catch (error: any) {
                console.error(error)
                const msg = this._driveTestSeqService.extractErrorMsg(error)
                this.warningMsg = msg
              }
            }
          }
          this.rootTest$.emit(this.rootTest)
        } else {
          if (seq.stopTime) {
            this.warningMsg = "Could not load dialer data"
          }
          this.loadFromDialerSub?.unsubscribe()

            this.loadFromDialerSub = this.dialerClientService.dialerService.initialized$.pipe(
              filter(bInit => bInit),
              first(),
              switchMap(_ => {
                this.loading = true;
                return this.dialerClientService.dialerTestService.findPiTestLogBySeqId(seq.dialerSeqId)
              })
            ).subscribe(
              testLogs => {
                this.loading = false
                const piTestLog = testLogs && testLogs[0]
                if (piTestLog && piTestLog.seqId == this.seq.dialerSeqId) {
                  this.rootTest = piTestLog.data
                  this.rootTest$.emit(this.rootTest)
                  this.warningMsg = "Dialer data missing in DB (loaded from dialer box)"
                }
              },
              error => {
                this.loading = false
                console.error(error)
                const errorMsg = 'Dialer data missing in DB and direct request for data from dialer also failed'
                this.warningMsg = errorMsg
                this.appState.publishErrorObj(error, errorMsg, this)
              },
              () => this.loading = false
            )


        }
      })

    } else if ( taskType == 'path test' ) {
      this.testSeqSubscription = this._testSeqService.rxCollection.findOne(seq.dialerSeqId).$.subscribe( async testSeqDoc => {
        this.loading = false
        this.warningMsg = null
        this.rootTest = null
        this.rootTest$.emit(this.rootTest)
        if ( testSeqDoc ) {
          this.rootTest = testSeqDoc.dialer
          // dialer data may be null to save space on clients...
          if ( !testSeqDoc.dialer && testSeqDoc.endTime ) {
            if ( this.appState.isOffline() ) {
              this.warningMsg = "Dialer data not available while offline"
            } else {
              try {
                const testSeq = await this._testSeqService.getByIdViaRest(testSeqDoc.seqid)
                console.log('getByIdViaRest()')
                console.dir(testSeq)
                if ( testSeq.dialer ) {
                  this.rootTest = testSeq.dialer
                }
              } catch ( error : any ) {
                console.error(error)
                const msg = this._testSeqService.extractErrorMsg(error)
                this.warningMsg = msg
              }
            }
          }
          this.rootTest$.emit(this.rootTest)
        } else {
          if ( seq.stopTime ) {
            this.warningMsg = "Could not load dialer data"
          }
          this.loadFromDialerSub?.unsubscribe()
          this.loadFromDialerSub = this.dialerClientService.dialerService.initialized$.pipe(
            filter( bInit => bInit ),
            first(),
            switchMap( _ => {
              this.loading = true;
              return this.dialerClientService.dialerTestService.findPiTestLogBySeqId(seq.dialerSeqId)
            })
          ).subscribe(
            testLogs => {
              this.loading = false
              const piTestLog = testLogs && testLogs[0]
              if ( piTestLog && piTestLog.seqId == this.seq.dialerSeqId ) {
                this.rootTest = piTestLog.data
                this.rootTest$.emit(this.rootTest)
                this.warningMsg = "Dialer data missing in DB (loaded from dialer box)"
              }
            },
            error => {
              this.loading = false
              console.error(error)
              const errorMsg = 'Dialer data missing in DB and direct request for data from dialer also failed'
              this.warningMsg = errorMsg
              this.appState.publishErrorObj(error,errorMsg,this)
            },
            () => this.loading = false
          )
        }
      })

    } else {
      throw new Error(`Expected task type of 'path test' or 'drive test' but found '${taskType}'`)
    }
  }

}
